import { memo, type ReactNode, type HTMLAttributes } from 'react'

/** Card variant options */
type CardVariant = 'default' | 'glass' | 'elevated' | 'flat'

/** Card padding options */
type CardPadding = 'none' | 'sm' | 'md' | 'lg'

/** Props for Card component */
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
  className?: string
  padding?: CardPadding
  variant?: CardVariant
}

/** Props for Card sub-components */
interface CardSubComponentProps {
  children?: ReactNode
  className?: string
}

const variantStyles: Record<CardVariant, string> = {
  default: 'bg-white dark:bg-gray-800 shadow-card border border-gray-100 dark:border-gray-700',
  glass: 'bg-white/85 dark:bg-gray-800/85 backdrop-blur-xl shadow-glass border border-white/20 dark:border-gray-700/50',
  elevated: 'bg-white dark:bg-gray-800 shadow-card hover:shadow-card-hover transition-shadow duration-200',
  flat: 'bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700',
}

const paddingStyles: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6'
}

const Card = memo(function Card({ children, className = '', padding = 'md', variant = 'default', ...props }: CardProps): JSX.Element {
  return (
    <div
      className={`rounded-xl ${variantStyles[variant]} ${paddingStyles[padding]} ${className}`}
      {...props}
    >
      {children}
    </div>
  )
})

function CardHeader({ children, className = '' }: CardSubComponentProps): JSX.Element {
  return (
    <div className={`mb-4 ${className}`}>
      {children}
    </div>
  )
}

function CardTitle({ children, className = '' }: CardSubComponentProps): JSX.Element {
  return (
    <h3 className={`text-lg font-semibold text-gray-900 dark:text-white ${className}`}>
      {children}
    </h3>
  )
}

function CardDescription({ children, className = '' }: CardSubComponentProps): JSX.Element {
  return (
    <p className={`text-sm text-gray-500 dark:text-gray-400 mt-1 ${className}`}>
      {children}
    </p>
  )
}

function CardContent({ children, className = '' }: CardSubComponentProps): JSX.Element {
  return (
    <div className={className}>
      {children}
    </div>
  )
}

function CardFooter({ children, className = '' }: CardSubComponentProps): JSX.Element {
  return (
    <div className={`mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 ${className}`}>
      {children}
    </div>
  )
}

Card.Header = CardHeader
Card.Title = CardTitle
Card.Description = CardDescription
Card.Content = CardContent
Card.Footer = CardFooter

export default Card
